import type { AfterResponseHook, BeforeRequestHook } from "@marketplace/http";

import { AuthService } from "@/modules/auth/services/auth-service";

import {
  checkIsPublicEndpoint,
  clearSession,
  getAbortSignal,
  getAccessToken,
  logout,
  retryRequest,
  setAccessToken
} from "./http-client-utils";

// Single-flight refresh promise to coalesce concurrent 401 handlers
let refreshPromise: Promise<string | null> | null = null;

export const beforeRequest: BeforeRequestHook[] = [
  async (request) => {
    const isPublicEndpoint = checkIsPublicEndpoint(request.url);

    if (isPublicEndpoint) {
      return request;
    }

    const accessToken = await getAccessToken();

    if (accessToken) {
      return request.headers.set("Authorization", `Bearer ${accessToken}`);
    }

    // Attach abort signal to request
    request = new Request(request, { signal: getAbortSignal() });

    // NOTE: consider this in the future
    if (!accessToken && !request.url.includes("refresh-token")) {
      return logout();
    }
  }
];

// Helper utilities to reduce complexity of afterResponse
function isRefreshRequest(url: string): boolean {
  return url.includes("refresh-token");
}

function isSigninRequest(url: string): boolean {
  return url.includes("signin");
}

function isLogoutRequest(url: string): boolean {
  return url.includes("logout");
}

function toObjectHeaders(
  headersInit: RequestInit["headers"]
): Record<string, string> {
  if (!headersInit) return {};
  if (headersInit instanceof Headers)
    return Object.fromEntries(headersInit.entries());
  if (Array.isArray(headersInit)) return Object.fromEntries(headersInit);
  return headersInit || {};
}

function withExtraHeader(
  options: RequestInit,
  key: string,
  value: string
): RequestInit {
  return {
    ...options,
    headers: {
      ...toObjectHeaders(options.headers),
      [key]: value
    }
  } as RequestInit;
}

async function refreshAccessTokenSingleFlight(): Promise<string | null> {
  refreshPromise ??= (async () => {
    try {
      const res = await AuthService.refreshToken();
      return res?.data?.access_token ?? null;
    } catch {
      return null;
    }
  })();
  const token = await refreshPromise;
  refreshPromise = null;
  return token;
}

export const afterResponse: AfterResponseHook[] = [
  async (request, options, response) => {
    const status = response.status;
    const url = request.url;

    // Any 400/401 from refresh-token should clear the session; avoid recursive refresh.
    // Only redirect and warn about an expired session if one actually existed.
    // Anonymous visitors (e.g. opening a public auth link) must stay on the current page.
    if ((status === 400 || status === 401) && isRefreshRequest(url)) {
      const hadSession = !!getAccessToken();

      return hadSession ? logout(true) : clearSession();
    }

    // Only handle non-signin/non-logout 401 responses
    if (status !== 401 || isSigninRequest(url) || isLogoutRequest(url)) {
      return;
    }

    // Skip token refresh for public endpoints
    if (checkIsPublicEndpoint(url)) {
      return;
    }

    // Prevent repeated refresh cycles for the same request
    if (request.headers.get("x-auto-refreshed") === "1") {
      return logout(true);
    }

    // Start or join a single refresh operation
    const newAccessToken = await refreshAccessTokenSingleFlight();

    if (newAccessToken) {
      await setAccessToken(newAccessToken);

      // Retry the original request with the new access token and mark as refreshed
      const nextOptions = withExtraHeader(options, "x-auto-refreshed", "1");
      return retryRequest(request, nextOptions, newAccessToken);
    }

    // Refresh failed
    return logout();
  }
];
